/**
 * AdaptiveResolution — mobile-only dynamic render-resolution governor.
 *
 * Task #274 follow-on (user-approved): instead of a flat pixel-ratio cap
 * (rejected — quality directive) or a per-character size list (rejected —
 * model file size doesn't predict per-pixel fill cost; a mid-size model
 * still slideshowed), the PHONE ITSELF decides, per session, in real time:
 *
 *   - Every character starts at FULL quality (the normal pixel ratio).
 *   - The governor watches the real interval between RENDERED frames
 *     (fed from ModelViewer.animate after renderer.render — actual
 *     measurements, never a guess).
 *   - It uses the MEDIAN over a rolling window, so one-off spikes (GC,
 *     texture upload, tab switch) never trigger anything.
 *   - Only when the median says "sustained slideshow" does it step the
 *     pixel ratio DOWN one notch. When the median says "comfortably
 *     smooth" for long enough, it steps back UP toward full quality.
 *   - Light characters that never drop frames therefore never change.
 *
 * Pure logic, no DOM/WebGL — unit-testable in Node. The caller applies
 * the returned multiplier to its base pixel ratio.
 */

// Quality ladder as multipliers of the BASE pixel ratio (min(DPR, 2)).
// On a DPR>=2 phone: 2.0 -> 1.75 -> 1.5 -> 1.25 -> 1.0 effective ratio.
// The caller floors the effective ratio at 1.0 (never below 1 CSS px).
export const SCALE_LADDER = [1, 0.875, 0.75, 0.625, 0.5];

const DEFAULTS = {
    // Healthy rendered cadence on the 24 FPS-capped mobile loop is
    // ~42-50 ms (vsync-quantized). Median above this = the user is
    // seeing jank worth trading resolution for (~<13 FPS).
    degradeAboveMs: 75,
    // Median at/below this = the loop is back on its healthy cadence.
    recoverBelowMs: 55,
    // A gap this large is a hidden tab / app switch, not a slow frame.
    ignoreGapAboveMs: 1000,
    // Evaluate at most this often, with at least this many samples.
    evalIntervalMs: 2000,
    minSamples: 12,
    windowSize: 40,
    // After any change, wait before the next downgrade...
    downgradeCooldownMs: 3000,
    // ...and require this much SUSTAINED healthy cadence to upgrade.
    upgradeHoldMs: 8000,
    // If an upgrade bounces (re-downgrade soon after), the next upgrade
    // must hold longer — doubles per bounce, capped.
    bounceWindowMs: 15000,
    upgradeHoldMaxMs: 60000,
};

export class AdaptiveResolutionGovernor {
    constructor(options = {}) {
        this._opts = Object.assign({}, DEFAULTS, options);
        this.reset();
    }

    /** Full reset — call on model swap / renderer re-init. */
    reset() {
        this._intervals = [];
        this._lastFrameAt = null;
        this._lastEvalAt = null;
        this._lastChangeAt = null;
        this._lastChangeDir = null;
        this._healthySinceMs = null;
        this._level = 0; // index into SCALE_LADDER; 0 = full quality
        this._requiredUpgradeHoldMs = this._opts.upgradeHoldMs;
        this._lastMedianMs = null;
    }

    /** Current multiplier for the base pixel ratio. */
    get scale() {
        return SCALE_LADDER[this._level];
    }

    /** Ladder position (0 = full quality). For the debug overlay. */
    get level() {
        return this._level;
    }

    /** Last evaluated median rendered-frame interval (ms), or null. */
    get medianIntervalMs() {
        return this._lastMedianMs;
    }

    /**
     * Feed one RENDERED frame (call right after renderer.render()).
     * Returns the new scale multiplier when a change should be applied,
     * or null when nothing changes (the overwhelmingly common case).
     */
    onFrameRendered(nowMs) {
        const o = this._opts;
        const prev = this._lastFrameAt;
        this._lastFrameAt = nowMs;
        if (prev == null) return null;

        const interval = nowMs - prev;
        // Hidden tab / app switch / model load stall: discard and also
        // drop the stale window — the phone's state may have changed.
        if (interval > o.ignoreGapAboveMs || interval <= 0) {
            this._intervals.length = 0;
            this._healthySinceMs = null;
            return null;
        }

        this._intervals.push(interval);
        if (this._intervals.length > o.windowSize) this._intervals.shift();

        if (this._lastEvalAt == null) this._lastEvalAt = nowMs;
        if (nowMs - this._lastEvalAt < o.evalIntervalMs) return null;
        if (this._intervals.length < o.minSamples) return null;
        this._lastEvalAt = nowMs;

        const median = this._median(this._intervals);
        this._lastMedianMs = median;

        const sinceChange =
            this._lastChangeAt == null ? Infinity : nowMs - this._lastChangeAt;

        // --- Downgrade path: sustained jank ---------------------------
        if (median > o.degradeAboveMs) {
            this._healthySinceMs = null;
            if (
                this._level < SCALE_LADDER.length - 1 &&
                sinceChange >= o.downgradeCooldownMs
            ) {
                // Bounce guard: if we just upgraded and are already
                // degrading again, make the NEXT upgrade prove itself
                // for longer before we retry.
                if (
                    this._lastChangeDir === "up" &&
                    sinceChange < o.bounceWindowMs
                ) {
                    this._requiredUpgradeHoldMs = Math.min(
                        this._requiredUpgradeHoldMs * 2,
                        o.upgradeHoldMaxMs,
                    );
                }
                this._level += 1;
                this._lastChangeAt = nowMs;
                this._lastChangeDir = "down";
                return this.scale;
            }
            return null;
        }

        // --- Upgrade path: sustained healthy cadence ------------------
        if (median <= o.recoverBelowMs && this._level > 0) {
            if (this._healthySinceMs == null) this._healthySinceMs = nowMs;
            if (nowMs - this._healthySinceMs >= this._requiredUpgradeHoldMs) {
                this._level -= 1;
                this._lastChangeAt = nowMs;
                this._lastChangeDir = "up";
                this._healthySinceMs = nowMs; // next step needs its own hold
                return this.scale;
            }
            return null;
        }

        // In-between (neither janky nor provably healthy): hold steady
        // and restart the healthy clock.
        if (median > o.recoverBelowMs) this._healthySinceMs = null;
        return null;
    }

    _median(arr) {
        const s = arr.slice().sort((a, b) => a - b);
        const mid = s.length >> 1;
        return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
    }
}

/**
 * Tiny debug readout, shown ONLY with ?fps=1 in the URL. Displays the
 * live FPS, the current effective pixel ratio, and the governor level so
 * on-device behavior can be verified without a devtools cable.
 */
export function createFpsOverlay() {
    if (typeof document === "undefined") return null;
    const el = document.createElement("div");
    el.id = "adaptive-res-fps";
    el.style.cssText = [
        "position:fixed",
        "top:calc(env(safe-area-inset-top, 0px) + 6px)",
        "left:6px",
        "z-index:99999",
        "pointer-events:none",
        "font:600 11px/1.4 -apple-system,system-ui,monospace",
        "color:#fff",
        "background:rgba(0,0,0,0.55)",
        "padding:3px 7px",
        "border-radius:6px",
        "letter-spacing:0.02em",
    ].join(";");
    el.textContent = "fps …";
    document.body.appendChild(el);
    return el;
}

export function isFpsOverlayRequested() {
    try {
        if (typeof window === "undefined") return false;
        return /[?&]fps=1\b/.test(window.location.search || "");
    } catch (_) {
        return false;
    }
}
